import extractWebsiteIdFromLoginUrl from '../../modules/websiteIdExtractor';
import redirectToUrl, { Redirector } from '../../modules/redirector';
import * as blobApi from '../../modules/blobApi';
import {
  AppState,
  getLang,
  getTemplateSubtype,
  getUsername,
  isMobile,
  getEasyMarketingTrackId,
} from '../reducers';
import { generalRequestFail, invalidUserIdToken } from './errors';
import { Action, AppDispatch, AppThunk, CallAsync } from '../../definitions/types';
import * as Actions from './actions-consts';
import { MOBILE_CMS_INFO_ENDPOINT } from '../../modules/constants';
import { Errors, handleError } from '../../modules/helpers';
import reload from '../../modules/reload';

export function createFreeWebsite(
  redirect: Redirector = redirectToUrl
): AppThunk {
  return async (dispatch: AppDispatch, getState: () => AppState, callAsync: CallAsync) => {
    const state = getState();

    if (state.user.idToken === '') {
      dispatch(invalidUserIdToken());
      reload();
      return;
    }

    // notify system that website creation process started
    dispatch(websiteCreationRequested());

    const usernameValue = getUsername(state);
    const lang = getLang(state);
    const subtype = getTemplateSubtype(state);
    const { ref, trial } = state.affiliate;
    const trsCookieValue = getEasyMarketingTrackId(state) || '';

    // create website with given data
    try {
      const response = await callAsync(
        blobApi.createWebsite,
        usernameValue,
        subtype,
        lang,
        state.user.idToken,
        ref,
        trial,
        trsCookieValue
      );

      if (response.status === 'error') {
        dispatch(websiteCreationFailed());
        handleError(dispatch, response.errors);
        return;
      }
      // website creation was successful
      dispatch(websiteCreationSucceeded(usernameValue));

      // try to retrieve login link for created website or redirect user to cms/mobile
      return dispatch(retrieveLoginLink(usernameValue, lang, redirect));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error);
      }
      dispatch(websiteCreationFailed());
      dispatch(generalRequestFail());
    }
  };
}

export function websiteCreationRequested(): Action<void> {
  return {
    type: Actions.WEBSITE_CREATION_REQUESTED,
    meta: {
      tracked: { action: 'website-creation', label: 'request' },
    },
  };
}

export function websiteCreationSucceeded(username: string): Action<{ username: string }> {
  return {
    type: Actions.WEBSITE_CREATION_SUCCESS,
    payload: {
      username,
    },
    meta: {
      tracked: { action: 'website-creation', label: 'success' },
    },
  };
}

export function websiteCreationFailed(): Action<void> {
  return {
    type: Actions.WEBSITE_CREATION_FAIL,
    meta: {
      tracked: { action: 'website-creation', label: 'fail' },
    },
  };
}

export interface LoginLinkRequestedPayload {
  username: string;
  timestamp: number;
}

export function loginLinkRequested(username: string): Action<LoginLinkRequestedPayload> {
  return {
    type: Actions.LOGIN_LINK_REQUESTED,
    payload: {
      username,
      timestamp: Date.now(),
    },
    meta: {
      tracked: { action: 'login-link', label: 'request' },
    },
  };
}

export function loginLinkPassed(url: string): Action<string> {
  return {
    type: Actions.LOGIN_LINK_PASSED,
    payload: url,
    meta: {
      tracked: { action: 'login-link', label: 'pass' },
    },
  };
}

export function websiteIdPassed(websiteId: string): Action<string> {
  return {
    type: Actions.WEBSITE_ID_PASSED,
    payload: websiteId,
    meta: {
      tracked: { action: 'website-id', label: 'pass' },
    },
  };
}

export function websiteIdFailed(error: string): Action<string> {
  return {
    type: Actions.WEBSITE_ID_FAILED,
    payload: error,
    error: true,
    meta: {
      tracked: { action: 'website-id', label: 'fail' },
    },
  };
}

export function websiteReady(): Action<void> {
  return {
    type: Actions.WEBSITE_READY,
    meta: {
      tracked: { action: 'website', label: 'ready' },
    },
  };
}

export function retrieveLoginLink(
  username: string,
  lang: string,
  redirect: Redirector = redirectToUrl
): AppThunk {
  return async (dispatch: AppDispatch, getState: () => AppState, callAsync: CallAsync) => {
    try {
      dispatch(loginLinkRequested(username));

      let loginUrl = await callAsync(blobApi.getLoginUrlForUsername, username, lang);
      const websiteId = extractWebsiteIdFromLoginUrl(loginUrl);
      if (isMobile(getState())) {
        loginUrl = `${MOBILE_CMS_INFO_ENDPOINT}/${websiteId}`;
      }
      dispatch(loginLinkPassed(loginUrl));
      dispatch(websiteIdPassed(websiteId));
      dispatch(websiteReady());

      redirect(loginUrl);
    } catch (e) {
      handleError(dispatch, e as Errors);
    }
  };
}
